import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

const G = (obj) => Object.entries(obj);

export default function AdminIntelligence() {
  const [d, setD] = useState(null);
  const [life, setLife] = useState(null);
  const [rules, setRules] = useState(null);
  const [recq, setRecq] = useState(null);
  const [refs, setRefs] = useState(null);
  const [ws, setWs] = useState(null);

  const load = () => {
    api.get("/admin/intelligence").then(({ data }) => setD(data));
    api.get("/admin/lifecycle").then(({ data }) => setLife(data));
    api.get("/admin/contact-eligibility").then(({ data }) => setRules(data.rules));
    api.get("/admin/recommendation-quality").then(({ data }) => setRecq(data)).catch(() => {});
    api.get("/admin/referrals").then(({ data }) => setRefs(data)).catch(() => {});
    api.get("/admin/workspace-analytics").then(({ data }) => setWs(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveLifecycle = async () => { try { await api.put("/admin/lifecycle-config", { config: life.config }); toast.success("Lifecycle thresholds saved"); } catch (e) { toast.error(apiError(e)); } };
  const saveRules = async () => { try { await api.put("/admin/contact-eligibility", { rules }); toast.success("Contact rules saved"); } catch (e) { toast.error(apiError(e)); } };

  if (!d || !life || !rules) return <FullSpinner label="Loading intelligence…" />;
  const Section = ({ title, obj }) => (
    <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4">
      <h2 className="font-serif text-lg font-semibold mb-3">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {G(obj).map(([k, v]) => (
          <div key={k} className="bg-[#1A090D] rounded-xl p-3">
            <div className="font-serif text-xl font-bold text-[#E2C382]">{typeof v === "number" && k === "revenue" ? `₹${v}` : v}</div>
            <div className="text-[11px] text-[#D8B4B9] capitalize">{k.replace(/_/g, " ")}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-intelligence">
      <h1 className="font-serif text-3xl font-bold">Intelligence Dashboard</h1>

      {d.alerts.length > 0 && (
        <div className="space-y-2" data-testid="intel-alerts">
          {d.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 rounded-xl p-3 text-sm ${a.level === "critical" ? "bg-rose-900/30 text-rose-200" : "bg-amber-900/30 text-amber-200"}`}>
              <AlertTriangle className="w-4 h-4" /> {a.text}
            </div>
          ))}
        </div>
      )}

      <Section title="Users & Lifecycle" obj={d.users} />
      <Section title="Discovery & Matches" obj={d.matches} />
      <Section title="Communication" obj={d.communication} />
      <Section title="Revenue & Membership" obj={d.revenue} />
      <Section title="Safety" obj={d.safety} />
      <Section title="Support" obj={d.support} />

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4">
          <h2 className="font-serif text-lg font-semibold mb-3">Lifecycle Thresholds</h2>
          {["new_period_days", "inactive_after_days", "dormant_after_days", "returning_window_days"].map((k) => (
            <label key={k} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-[#D8B4B9] capitalize">{k.replace(/_/g, " ")}</span>
              <input type="number" value={life.config[k] ?? ""} data-testid={`life-cfg-${k}`}
                onChange={(e) => setLife({ ...life, config: { ...life.config, [k]: Number(e.target.value) } })}
                className="w-20 bg-[#1A090D] border border-[#781826]/40 rounded-lg px-2 py-1 text-white text-right" />
            </label>
          ))}
          <button onClick={saveLifecycle} data-testid="save-lifecycle" className="btn-gold text-sm mt-3 px-4 py-2 rounded-lg">Save thresholds</button>
        </div>

        <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4">
          <h2 className="font-serif text-lg font-semibold mb-3">Contact Eligibility Rules</h2>
          {["require_verification", "require_profile_completion", "require_premium", "require_mutual_interest", "recipient_approval"].map((k) => (
            <label key={k} className="flex items-center justify-between py-1.5 text-sm cursor-pointer">
              <span className="text-[#D8B4B9] capitalize">{k.replace(/_/g, " ")}</span>
              <input type="checkbox" checked={!!rules[k]} data-testid={`rule-${k}`} onChange={(e) => setRules({ ...rules, [k]: e.target.checked })} className="w-4 h-4 accent-[#C59B27]" />
            </label>
          ))}
          {["completion_threshold", "daily_limit", "request_expiry_days"].map((k) => (
            <label key={k} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-[#D8B4B9] capitalize">{k.replace(/_/g, " ")}</span>
              <input type="number" value={rules[k] ?? ""} data-testid={`rule-${k}`} onChange={(e) => setRules({ ...rules, [k]: Number(e.target.value) })}
                className="w-20 bg-[#1A090D] border border-[#781826]/40 rounded-lg px-2 py-1 text-white text-right" />
            </label>
          ))}
          <button onClick={saveRules} data-testid="save-rules" className="btn-gold text-sm mt-3 px-4 py-2 rounded-lg">Save rules</button>
        </div>
      </div>

      {recq && (
        <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4" data-testid="intel-recq">
          <h2 className="font-serif text-lg font-semibold mb-3">Recommendation Quality</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Impressions", recq.impressions], ["Unique profiles", recq.unique_profiles], ["Avg repeat", recq.avg_repeat_per_profile], ["Stale (30d+)", recq.stale_over_30d], ["Interests", recq.interests], ["Accept rate", `${recq.acceptance_rate}%`], ["Shortlists", recq.shortlists], ["Feedback", recq.feedback_total]].map(([l, v]) => (
              <div key={l} className="bg-[#1A090D] rounded-xl p-3"><div className="font-serif text-xl font-bold text-[#E2C382]">{v}</div><div className="text-[11px] text-[#D8B4B9]">{l}</div></div>
            ))}
          </div>
          {recq.top_not_interested_reasons?.length > 0 && (
            <div className="mt-3 text-xs text-[#D8B4B9]">Top "not interested" reasons: {recq.top_not_interested_reasons.slice(0, 4).map((r) => `${r.reason} (${r.count})`).join(" · ")}</div>
          )}
        </div>
      )}

      {refs && (
        <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4" data-testid="intel-referrals">
          <h2 className="font-serif text-lg font-semibold mb-3">Referrals</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            <div className="bg-[#1A090D] rounded-xl p-3"><div className="font-serif text-xl font-bold text-[#E2C382]">{refs.total_referrals}</div><div className="text-[11px] text-[#D8B4B9]">Total</div></div>
            {Object.entries(refs.stages).map(([k, v]) => (
              <div key={k} className="bg-[#1A090D] rounded-xl p-3"><div className="font-serif text-xl font-bold text-[#E2C382]">{v}</div><div className="text-[10px] text-[#D8B4B9] capitalize">{k.replace(/_/g, " ").toLowerCase()}</div></div>
            ))}
          </div>
          {refs.top_referrers?.length > 0 && (
            <div className="mt-3 text-xs text-[#D8B4B9]">Top inviters: {refs.top_referrers.slice(0, 5).map((t) => `${t.name} (${t.invites})`).join(" · ")}</div>
          )}
        </div>
      )}

      {ws && (
        <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4" data-testid="intel-workspace">
          <h2 className="font-serif text-lg font-semibold mb-3">Jodi Decision Workspace</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {[["Active workspaces", ws.active_workspaces], ["Shortlist users", ws.shortlist_users], ["Total shortlisted", ws.total_shortlisted], ["Collections", ws.collections], ["Custom collections", ws.custom_collections], ["Family reviews", ws.family_review], ["Reminders", ws.reminders], ["Meetings planned", ws.meetings_planned]].map(([l, v]) => (
              <div key={l} className="bg-[#1A090D] rounded-xl p-3"><div className="font-serif text-xl font-bold text-[#E2C382]">{v}</div><div className="text-[10px] text-[#D8B4B9]">{l}</div></div>
            ))}
          </div>
          <div className="mt-3 text-xs text-[#D8B4B9]">
            Engagement: {Object.entries(ws.events).map(([k, v]) => `${k.replace(/_/g, " ")} (${v})`).join(" · ")}
          </div>
        </div>
      )}

      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4">
        <h2 className="font-serif text-lg font-semibold mb-3">Lifecycle Distribution</h2>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {G(life.distribution).map(([k, v]) => (
            <div key={k} className="bg-[#1A090D] rounded-xl p-3">
              <div className="font-serif text-lg font-bold text-[#E2C382]">{v}</div>
              <div className="text-[10px] text-[#D8B4B9] capitalize">{k.replace(/_/g, " ").toLowerCase()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
